import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useOffFeed } from "@/context/OffFeedContext";
import { DISPATCHES, shuffled } from "@/data/offFeed";

/* ------------------------------------------------------------------ */
/*  Category typography — the whole visual identity lives here.        */
/* ------------------------------------------------------------------ */

const CATEGORY_META = {
    THOUGHT:     { chip: "text-abnormal-acid",     rule: "border-abnormal-acid/60" },
    QUESTION:    { chip: "text-white/50",         rule: "border-white/20" },
    PROVOCATION: { chip: "text-abnormal-acid",     rule: "border-abnormal-acid/60" },
    QUOTE:       { chip: "text-white/50",         rule: "border-white/20" },
    FIELD_NOTE:  { chip: "text-abnormal-acid/70",  rule: "border-white/10" },
};

const CATEGORY_LABEL = {
    THOUGHT:     "// THOUGHT",
    QUESTION:    "// QUESTION",
    PROVOCATION: "// PROVOCATION",
    QUOTE:       "// QUOTE",
    FIELD_NOTE:  "// FIELD NOTE",
};

/** Content typography by category — everything else is silence. */
function DispatchBody({ category, text, size }) {
    const scale = {
        sm: "text-xl md:text-2xl",
        md: "text-2xl md:text-3xl lg:text-[32px]",
        lg: "text-3xl md:text-4xl lg:text-5xl",
    }[size || "md"];

    switch (category) {
        case "QUESTION":
            return (
                <p className={`font-display uppercase tracking-tight leading-[1.05] italic text-zinc-100 ${scale}`}>
                    {text}
                </p>
            );
        case "PROVOCATION":
            return (
                <p className={`font-display uppercase tracking-tight leading-[1.02] text-abnormal-acid ${scale}`}>
                    {text}
                </p>
            );
        case "QUOTE":
            return (
                <p className={`font-display italic normal-case tracking-tight leading-[1.15] text-zinc-100 ${scale}`}>
                    {text}
                </p>
            );
        case "FIELD_NOTE":
            return (
                <p className="font-vt text-lg md:text-xl leading-[1.4] tracking-wide text-zinc-300">
                    {text}
                </p>
            );
        case "THOUGHT":
        default:
            return (
                <p className={`font-display uppercase tracking-tight leading-[1.02] text-white ${scale}`}>
                    {text}
                </p>
            );
    }
}

/* ------------------------------------------------------------------ */
/*  The room.                                                          */
/*                                                                     */
/*  Accessibility & lifecycle discipline:                              */
/*   - Escape closes (document capture-phase listener, single handler) */
/*   - Focus trap: Tab / Shift+Tab cycles inside                       */
/*   - Focus moves to close button on open; returns to trigger on close*/
/*   - Body scroll locked while open, restored on close                */
/*   - When `open === false` the whole portal returns null → DOM gone  */
/* ------------------------------------------------------------------ */

export default function OffFeed() {
    const { open, closeOffFeed, triggerRef } = useOffFeed();
    const { lang } = useLang();

    const scrollRef = useRef(null);
    const overlayRef = useRef(null);
    const closeButtonRef = useRef(null);

    // Shuffle once per open so the room breathes differently each time.
    const list = useMemo(() => (open ? shuffled(DISPATCHES) : []), [open]);

    // All lifecycle side-effects, gated on `open`.
    useEffect(() => {
        if (!open) return undefined;

        // 1) Remember the trigger element for focus restore. Fall back to the
        //    currently focused element if the caller didn't pass one.
        const trigger = triggerRef.current
            || (document.activeElement instanceof HTMLElement ? document.activeElement : null);

        // 2) Lock body scroll (remember previous value).
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        // 3) Move focus into the room after the portal mounts.
        const focusTimer = window.setTimeout(() => {
            closeButtonRef.current?.focus();
        }, 0);

        // 4) Reset internal scroll.
        if (scrollRef.current) scrollRef.current.scrollTop = 0;

        // 5) Keyboard handler (document + capture phase for reliability).
        const handleKey = (e) => {
            if (e.key === "Escape" || e.key === "Esc") {
                e.preventDefault();
                e.stopPropagation();
                closeOffFeed();
                return;
            }
            if (e.key === "Tab") {
                // Simple focus trap inside the overlay.
                const container = overlayRef.current;
                if (!container) return;
                const focusables = Array.from(
                    container.querySelectorAll(
                        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
                    )
                );
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener("keydown", handleKey, true);

        // Cleanup runs when `open` becomes false OR the component unmounts.
        return () => {
            document.removeEventListener("keydown", handleKey, true);
            document.body.style.overflow = prevOverflow;
            window.clearTimeout(focusTimer);
            // Restore focus to the trigger, if it still exists in the DOM.
            if (trigger && document.body.contains(trigger) && typeof trigger.focus === "function") {
                try { trigger.focus(); } catch (_) { /* ignore */ }
            }
            triggerRef.current = null;
        };
    }, [open, closeOffFeed, triggerRef]);

    if (typeof document === "undefined") return null;
    if (!open) return null;

    const totalLabel = lang === "en"
        ? `${DISPATCHES.length} dispatches · shuffled`
        : `${DISPATCHES.length} dispatch · rimescolati`;
    const outroLabel = lang === "en"
        ? "// end of transmission"
        : "// fine trasmissione";

    const onBackdropClick = (e) => {
        // Backdrop click closes; content click doesn't
        if (e.target === e.currentTarget) closeOffFeed();
    };

    return createPortal(
        <div
            ref={overlayRef}
            data-testid="off-feed-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="off-feed-title"
            tabIndex={-1}
            className="fixed inset-0 z-[9999] bg-black text-white off-feed-enter"
            onClick={onBackdropClick}
        >
            {/* Header — sober, aware of its own presence */}
            <header
                className="sticky top-0 z-10 bg-black/95 backdrop-blur-sm border-b border-white/10"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-5 flex items-center justify-between gap-6">
                    <div className="flex items-baseline gap-4">
                        <p id="off-feed-title" className="font-vt text-sm md:text-base tracking-[0.4em] uppercase text-abnormal-acid">
                            // OFF-FEED
                        </p>
                        <p className="hidden md:block font-vt text-[11px] tracking-[0.3em] uppercase text-abnormal-paperDim/60">
                            {totalLabel}
                        </p>
                    </div>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        data-testid="off-feed-close"
                        onClick={closeOffFeed}
                        aria-label={lang === "en" ? "Close Off-Feed" : "Chiudi Off-Feed"}
                        className="group inline-flex items-center gap-2 font-vt text-[11px] tracking-[0.3em] uppercase text-white/60 hover:text-white focus:outline-none focus:text-white focus-visible:ring-1 focus-visible:ring-white/40 transition-colors px-2 py-1"
                    >
                        <span className="hidden sm:inline">esc</span>
                        <X size={16} className="group-hover:rotate-90 transition-transform duration-300" />
                    </button>
                </div>
            </header>

            {/* Body — scrollable room */}
            <div
                ref={scrollRef}
                className="overflow-y-auto max-h-[calc(100vh-64px)] off-feed-scroll"
                onClick={onBackdropClick}
            >
                <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-10 pb-24">
                    <p className="font-vt text-[11px] md:text-sm tracking-[0.4em] uppercase text-white/40 mb-14 md:mb-20">
                        {lang === "en"
                            ? "// no algorithms — only ideas"
                            : "// nessun algoritmo — solo idee"}
                    </p>

                    <div className="off-feed-columns">
                        {list.map((d) => {
                            const meta = CATEGORY_META[d.category] || CATEGORY_META.THOUGHT;
                            const text = lang === "en" ? d.en : d.it;
                            return (
                                <article
                                    key={d.id}
                                    data-testid={`off-feed-dispatch-${d.id}`}
                                    data-category={d.category}
                                    className={`off-feed-card border-t ${meta.rule} pt-4 md:pt-5 pb-10 md:pb-14`}
                                >
                                    <p className={`font-vt text-[10px] tracking-[0.35em] uppercase mb-4 ${meta.chip}`}>
                                        {CATEGORY_LABEL[d.category] || `// ${d.category}`}
                                    </p>
                                    <DispatchBody category={d.category} text={text} size={d.size} />
                                </article>
                            );
                        })}
                    </div>

                    <div className="mt-16 md:mt-24 border-t border-white/10 pt-6 flex items-center justify-between">
                        <p className="font-vt text-[11px] tracking-[0.4em] uppercase text-white/30">
                            {outroLabel}
                        </p>
                        <button
                            type="button"
                            onClick={closeOffFeed}
                            className="font-vt text-[11px] tracking-[0.4em] uppercase text-white/40 hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-white/40 transition-colors px-2 py-1"
                        >
                            {lang === "en" ? "leave the room" : "esci dalla stanza"}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes off-feed-in {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                .off-feed-enter {
                    animation: off-feed-in 180ms ease-out;
                }
                .off-feed-columns {
                    column-count: 1;
                    column-gap: 3rem;
                }
                @media (min-width: 768px) {
                    .off-feed-columns { column-count: 2; }
                }
                @media (min-width: 1100px) {
                    .off-feed-columns { column-count: 3; }
                }
                .off-feed-card {
                    break-inside: avoid;
                    -webkit-column-break-inside: avoid;
                    page-break-inside: avoid;
                }
                .off-feed-scroll::-webkit-scrollbar { width: 6px; }
                .off-feed-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); }
                .off-feed-scroll::-webkit-scrollbar-track { background: transparent; }
                @media (prefers-reduced-motion: reduce) {
                    .off-feed-enter { animation: none; }
                }
            `}</style>
        </div>,
        document.body
    );
}
